import * as React from 'react'

import { cn } from '@/registry/reapit/lib/utils'
import { Flex, Stack } from '@/registry/reapit/ui/flex'
import { Button } from '@/registry/reapit/ui/button'
import { SearchInput } from '@/registry/reapit/ui/search-input'

// Re-export for convenience — consumers may compose ChipGroup inside FilterBarApplied
export { ChipGroup } from '@/registry/reapit/ui/chip'

// ---------------------------------------------------------------------------
// FilterBar — root wrapper
// DS: vertical stack — controls row above applied filters row
// ---------------------------------------------------------------------------

type FilterBarProps = React.ComponentProps<'div'>

function FilterBar({ className, children, ...props }: FilterBarProps) {
  return (
    <Stack data-slot="filter-bar" gap={3} className={cn('w-full', className)} {...props}>
      {children}
    </Stack>
  )
}

// ---------------------------------------------------------------------------
// FilterBarTopRow — flex row containing search, actions and controls
// DS: left content grows to fill; right content stays fixed
// ---------------------------------------------------------------------------

type FilterBarTopRowProps = React.ComponentProps<'div'>

function FilterBarTopRow({ className, children, ...props }: FilterBarTopRowProps) {
  return (
    <Flex data-slot="filter-bar-top-row" align="center" gap={2} className={cn('flex-wrap', className)} {...props}>
      {children}
    </Flex>
  )
}

// ---------------------------------------------------------------------------
// FilterBarSearch — grows to fill available space, renders SearchInput
// ---------------------------------------------------------------------------

type FilterBarSearchProps = Omit<React.ComponentProps<typeof SearchInput>, 'size'> & {
  size?: 'small' | 'medium' | 'large'
  className?: string
}

function FilterBarSearch({ className, size = 'medium', ...props }: FilterBarSearchProps) {
  return (
    <div data-slot="filter-bar-search" className={cn('min-w-0 flex-1', className)}>
      <SearchInput size={size} {...props} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// FilterBarActions — action buttons (e.g. "Filters", "Sort")
// ---------------------------------------------------------------------------

type FilterBarActionsProps = React.ComponentProps<'div'>

function FilterBarActions({ className, children, ...props }: FilterBarActionsProps) {
  return (
    <Flex data-slot="filter-bar-actions" align="center" gap={2} className={cn(className)} {...props}>
      {children}
    </Flex>
  )
}

// ---------------------------------------------------------------------------
// FilterBarControls — rightmost controls (view switcher, toggles, etc.)
// ---------------------------------------------------------------------------

type FilterBarControlsProps = React.ComponentProps<'div'>

function FilterBarControls({ className, children, ...props }: FilterBarControlsProps) {
  return (
    <Flex data-slot="filter-bar-controls" align="center" gap={2} className={cn(className)} {...props}>
      {children}
    </Flex>
  )
}

// ---------------------------------------------------------------------------
// FilterBarApplied — applied filters row (chips + clear/save actions)
// DS: filter chips on the left; "Clear all" and "Save filters" actions follow inline
// ---------------------------------------------------------------------------

type FilterBarAppliedProps = React.ComponentProps<'div'> & {
  onClearAll?: () => void
  showSaveFilters?: boolean
  onSaveFilters?: () => void
}

function FilterBarApplied({
  className,
  children,
  onClearAll,
  showSaveFilters = false,
  onSaveFilters,
  ...props
}: FilterBarAppliedProps) {
  const hasChildren = React.Children.count(children) > 0

  if (!hasChildren) return null

  return (
    <Flex data-slot="filter-bar-applied" align="center" gap={2} className={cn('flex-wrap', className)} {...props}>
      {children}
      {onClearAll && (
        <Button data-slot="filter-bar-clear-all" variant="tertiary" size="small" onClick={onClearAll} type="button">
          Clear all
        </Button>
      )}
      {showSaveFilters && onSaveFilters && (
        <Button
          data-slot="filter-bar-save-filters"
          variant="tertiary"
          size="small"
          onClick={onSaveFilters}
          type="button"
        >
          Save filters
        </Button>
      )}
    </Flex>
  )
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { FilterBar, FilterBarTopRow, FilterBarSearch, FilterBarActions, FilterBarControls, FilterBarApplied }

export type {
  FilterBarProps,
  FilterBarTopRowProps,
  FilterBarSearchProps,
  FilterBarActionsProps,
  FilterBarControlsProps,
  FilterBarAppliedProps,
}
